import React, { useEffect, useState } from 'react';
import '../beforeLoading/BeforeLoading.less';
import { Button, Spin, Select, Form, Input } from 'antd';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@isocel/front-common';

import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { fetchProvider } from '../../queries/api/palette';
import { TypeofMovements } from '../../constants';
import { PackagingFlowDirection } from '../../queries/api/types';
import { usePalettes, useSupplierByOrganization, useWarehousesByOrganization } from '../../queries/palette';
import { useNotification } from '../notification/Notification';
import { useStoreByOrganization } from '../../queries/store';

const { Option } = Select;

const PalettCreate: React.FC = React.memo(() => {
    const {
        movementType,
        setMovementType,
        selectedProvider,
        setSelectedProvider,
        selectedParc,
        setSelectedParc,
        providers,
        setProviders,
        parcPalettes,
        setParcPalettes,
        setFlowType,
        selectedSender,
        setSelectedSender,
        selectedReciever,
        setSelectedReciever,
        selectedStoreMovement,
        setSelectedStoreMovement,
        selectedSupplier,
        setSelectedSupplier,
        setModifiedCounterName,
        modifiedCounterName,
        reset,
    } = usePackagingFlow();

    const history = useHistory();
    const { user } = useAuth();
    const [isTransfer, setIsTransfer] = useState(false);
    const [providerVisible, setProviderVisible] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true); // State to control button disable/enable
    const [isCounterModified, setCounterModified] = useState(false);
    const [isParcSelected, setParcSelected] = useState(false);
    const [isStoreSelected, setStoreSelected] = useState(false);
    const [isSupplierSelected, setSupplierSelected] = useState(false);

    const { data: palettesData } = usePalettes(user?.organization?.id || '');
    const { data: wareHouses } = useWarehousesByOrganization(user?.organization?.id || '');
    const { data: suppliers } = useSupplierByOrganization(user?.organization?.id || '');
    const { data: storeList } = useStoreByOrganization(user?.organization?.id ?? '');
    const issuerList = [
        ...(palettesData?.map((item) => ({ ...item, SelectionType: 'palette' })) || []),
        ...(wareHouses?.map((item) => ({ ...item, SelectionType: 'wareHouse' })) || []),
    ];
    const recipientList = [
        ...(palettesData?.map((item) => ({ ...item, SelectionType: 'palette' })) || []),
        ...(wareHouses?.map((item) => ({ ...item, SelectionType: 'wareHouse' })) || []),
        ...(suppliers?.map((item) => ({ ...item, SelectionType: 'supplier' })) || []),
    ];

    const counterSelectionList = [
        {
            id: 'store',
            name: 'Compteur magasin',
        },
        {
            id: 'supplier',
            name: 'Dette Transporteur',
        },
        {
            id: 'parc',
            name: 'Compteur parc palettes',
        },
    ];
    const { showNotification } = useNotification();
    useEffect(() => {
        reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const isProviderValid = movementType && selectedParc && !isTransfer;
        const isTransferValid = movementType && selectedSender && selectedReciever && isTransfer;
        const isCounterModifiedValid =
            movementType &&
            modifiedCounterName &&
            ((isParcSelected && selectedParc) ||
                (isStoreSelected && selectedStoreMovement) ||
                (isSupplierSelected && selectedSupplier));
        const isDisabled = providerVisible
            ? (isProviderValid && !!selectedProvider) || isTransferValid || isCounterModifiedValid
            : isProviderValid || isTransferValid || isCounterModifiedValid;

        setIsButtonDisabled(!isDisabled);
    }, [
        movementType,
        selectedProvider,
        selectedParc,
        selectedSender,
        selectedReciever,
        isTransfer,
        providerVisible,
        modifiedCounterName,
        isParcSelected,
        isStoreSelected,
        isSupplierSelected,
        selectedStoreMovement,
        selectedSupplier,
    ]);

    const handleMovementTypeChange = async (value: string) => {
        const movements = TypeofMovements.find((p) => p.label === value) || null;
        setMovementType(movements);
        setIsTransfer(movements?.name === 'Transfer');

        if (movements?.name === 'Destruction') {
            setProviderVisible(false);
        } else {
            setProviderVisible(true);
        }
        if (movements?.name === 'Increase' || movements?.name === 'Decrease') {
            setCounterModified(true);
        } else {
            setCounterModified(false);
            setSelectedParc(null); // Now allowed due to updated type
        }
        try {
            const providerData = await fetchProvider();
            setProviders(providerData);
            setParcPalettes(palettesData || []);
            if (palettesData?.length === 1) {
                setSelectedParc(palettesData[0]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const handleProviderChange = (value: string) => {
        const provider = providers.find((p) => p.name === value) || null;
        setSelectedProvider(provider);
    };

    const handleParcChange = (value: string) => {
        const parc = parcPalettes.find((p) => p.name === value) || null;
        setSelectedParc(parc);
        setSelectedReciever({ ...parc, SelectionType: 'palette' });
    };

    const handleSenderChange = (value: string) => {
        const issuer = issuerList.find((p) => p.name === value) || null;
        if (issuer) {
            setSelectedSender(issuer);
        }
    };

    const handleRecieverChange = (value: string) => {
        const recipient = recipientList.find((r) => r.name === value) || null;
        if (recipient) {
            setSelectedReciever(recipient);
        }
    };

    const modifyCount = (value: string) => {
        const selectedCounter = counterSelectionList.find((counter) => counter.name === value);
        setStoreSelected(selectedCounter?.id === 'store');
        setParcSelected(selectedCounter?.id === 'parc');
        setSupplierSelected(selectedCounter?.id === 'supplier');
        setModifiedCounterName(selectedCounter?.name || '');
        if (user?.organization) {
            setSelectedSender({ ...user.organization, SelectionType: 'SCA' });
        }
    };

    const handleStoreChange = (value: string) => {
        const store = storeList?.find((s) => s.name === value) || null;
        if (store) {
            setSelectedStoreMovement(store);
            setSelectedReciever({ ...store, SelectionType: 'store' });
        }
    };
    const handleSupplierChange = (value: string) => {
        const supplier = suppliers?.find((s) => s.name === value) || null;
        if (supplier) {
            setSelectedSupplier(supplier);
            setSelectedReciever({ ...supplier, SelectionType: 'supplier' });
        }
    };
    const handleSubmit = () => {
        if (selectedSender && selectedReciever && selectedSender.name === selectedReciever.name) {
            showNotification(
                'error',
                "Échec de l'analyse",
                'Veuillez choisir un émetteur et un destinataire différent'
            );
            return;
        }
        const flowTypeMap = {
            Purchase: PackagingFlowDirection.Purchase,
            Sale: PackagingFlowDirection.Sale,
            Destruction: PackagingFlowDirection.Destruction,
            ReceptionToRepair: PackagingFlowDirection.ReceptionFromRepair,
            ShipmentToRepair: PackagingFlowDirection.ShipmentForRepair,
            Transfer: PackagingFlowDirection.Transfer,
            Increase: PackagingFlowDirection.Increase,
            Decrease: PackagingFlowDirection.Decrease,
        } as const;

        const selectedFlowType =
            movementType?.name && movementType.name in flowTypeMap
                ? flowTypeMap[movementType.name as keyof typeof flowTypeMap]
                : PackagingFlowDirection.ShipmentForRepair;

        setFlowType(selectedFlowType);

        const receptionFlows = [PackagingFlowDirection.Purchase, PackagingFlowDirection.ReceptionFromRepair];
        const nextRoute = receptionFlows.includes(selectedFlowType) ? './recipientTrack' : './recipientTrack';

        history.push(nextRoute);
    };

    return (
        <div className="before-loading-page">
            <div className="title">
                <h2>Mouvements Divers</h2>
            </div>
            <Form layout="vertical" onFinish={handleSubmit}>
                <Form.Item label="Type de mouvement">
                    <Select
                        id="movement-type"
                        placeholder="Sélectionner le type de mouvement"
                        notFoundContent="Aucun résultat"
                        value={movementType?.label}
                        onChange={handleMovementTypeChange}
                        className="dropdown-input"
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {TypeofMovements.map((movement) => (
                            <Option key={movement.name} value={movement.label}>
                                {movement.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {movementType && !isTransfer && !isCounterModified && (
                    <>
                        <Spin spinning={false}></Spin>
                        {providerVisible && (
                            <Form.Item label="Libellé du prestataire">
                                <Select
                                    id="prestataire"
                                    placeholder="Choisir le prestataire"
                                    value={selectedProvider?.name}
                                    notFoundContent="Aucun résultat"
                                    onChange={handleProviderChange}
                                    className="dropdown-input"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {providers.map((provider) => (
                                        <Option key={provider.id} value={provider.name}>
                                            {provider.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                        <Form.Item label="Parc palettes">
                            {palettesData?.length === 1 ? (
                                <Input value={palettesData[0].name} className="dropdown-input" />
                            ) : (
                                <Select
                                    id="parcs"
                                    placeholder="Choisir le parc palettes"
                                    value={selectedParc?.name}
                                    notFoundContent="Aucun résultat"
                                    onChange={handleParcChange}
                                    className="dropdown-input"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {parcPalettes.map((parc) => (
                                        <Option key={parc.id} value={parc.name}>
                                            {parc.name}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                    </>
                )}

                {/* transfer */}

                {movementType && isTransfer && !isCounterModified && (
                    <>
                        <Spin spinning={false}></Spin>
                        <Form.Item label="Libellé de l'émetteur">
                            <Select
                                id="sender"
                                placeholder="Choisir l'émetteur"
                                value={selectedSender?.name}
                                notFoundContent="Aucun résultat"
                                onChange={handleSenderChange}
                                className="dropdown-input"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {issuerList.map((issuer) => (
                                    <Option key={issuer.id} value={issuer.name}>
                                        {issuer.SelectionType === 'palette'
                                            ? `Parc palette- ${issuer.name}`
                                            : issuer.SelectionType === 'wareHouse'
                                            ? `Entrepôt- ${issuer.name}`
                                            : issuer.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Libellé du destinataire">
                            <Select
                                id="reciever"
                                placeholder="Choisir le destinataire"
                                value={selectedReciever?.name}
                                notFoundContent="Aucun résultat"
                                onChange={handleRecieverChange}
                                className="dropdown-input"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {recipientList.map((recipient) => (
                                    <Option key={recipient.id} value={recipient.name}>
                                        {recipient.SelectionType === 'palette'
                                            ? `Parc palette- ${recipient.name}`
                                            : recipient.SelectionType === 'wareHouse'
                                            ? `Entrepôt- ${recipient.name}`
                                            : recipient.SelectionType === 'supplier'
                                            ? `Transporteur- ${recipient.name}`
                                            : recipient.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </>
                )}

                {/* counterChange */}
                {isCounterModified && (
                    <>
                        <Form.Item label="Compteur impacté">
                            <Select
                                id="sender"
                                placeholder="Compteur impacté"
                                value={modifiedCounterName}
                                notFoundContent="Aucun résultat"
                                onChange={modifyCount}
                                className="dropdown-input"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {counterSelectionList.map(({ id, name }) => (
                                    <Option key={id} value={name}>
                                        {name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {isParcSelected && (
                            <Form.Item label="Libellé du parc palettes">
                                <Select
                                    id="parc"
                                    placeholder="Choisir le parc palettes"
                                    value={selectedParc?.name}
                                    notFoundContent="Aucun résultat"
                                    onChange={handleParcChange}
                                    className="dropdown-input"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {parcPalettes.map(({ id, name }) => (
                                        <Option key={id} value={name}>
                                            {name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                        {isStoreSelected && (
                            <Form.Item label="Magasin">
                                <Select
                                    id="store"
                                    placeholder="Choisir le Magasin"
                                    value={selectedStoreMovement?.name}
                                    notFoundContent="Aucun résultat"
                                    onChange={handleStoreChange}
                                    className="dropdown-input"
                                    showSearch
                                    filterOption={(input, option) => {
                                        // Option value is code - name
                                        const text = (option?.children as unknown as string).toLowerCase();
                                        return text.includes(input.toLowerCase());
                                    }}
                                >
                                    {storeList?.map(({ id, code, name }) => (
                                        <Option key={id} value={name}>
                                            {code ? `${code} - ${name}` : name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                        {isSupplierSelected && (
                            <Form.Item label="Transporteur">
                                <Select
                                    id="supplier"
                                    placeholder="Choisir le Transporteur"
                                    value={selectedSupplier?.name}
                                    notFoundContent="Aucun résultat"
                                    onChange={handleSupplierChange}
                                    className="dropdown-input"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {suppliers?.map(({ id, name }) => (
                                        <Option key={id} value={name}>
                                            {name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </>
                )}
                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        disabled={isButtonDisabled} // Disable button based on condition
                    >
                        Suivant
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
});

export default PalettCreate;
